import {
  addMinutes,
  format,
  formatDistanceToNow,
  getYear,
  subDays,
} from "date-fns";

export * from "date-fns";

const thisYear = getYear(new Date());

// June 30, 8:50AM
export const FormatDate = (str: string | null | undefined): string => {
  if (str == null) return "";
  if (str == "") return str;
  const year = getYear(new Date(str));
  if (year <= 1) return "Never";

  const m = new Date(str);
  if (getYear(m) < thisYear || getYear(m) > thisYear)
    return format(m, "MMMM dd, yyyy");
  return format(m, "MMMM dd, hh:mm a");
};

// 11/30/2021, 8:50AM
export const FormatDateShort = (str: string | null | undefined): string => {
  if (str == null) return "";
  if (str == "") return str;
  const year = getYear(new Date(str));
  if (year <= 1) return "Never";

  const m = new Date(str);
  if (getYear(m) < thisYear || getYear(m) > thisYear)
    return format(m, "MM/dd/yyyy, hh:mm a");
  return format(m, "MM/dd, hh:mm a");
};

// June 30, 2021
export const FormatDateYear = (str: string | null | undefined): string => {
  if (str == null) return "";
  if (str == "") return str;
  const year = getYear(new Date(str));
  if (year <= 1) return "Never";

  const m = new Date(str);
  return format(m, "MMMM dd, yyyy");
};

// June 30, 2021, 8:50AM
export const FormatDateYearTime = (str: string | null | undefined): string => {
  if (str == null) return "";
  if (str == "") return str;
  const year = getYear(new Date(str));
  if (year <= 1) return "Never";

  const m = new Date(str);
  return format(m, "MMMM dd, yyyy, hh:mm a");
};

// Feb 6, 2021
export const FormatAbbrDateYear = (str: string | null | undefined): string => {
  if (str == null) return "";
  if (str == "") return str;
  const year = getYear(new Date(str));
  if (year <= 1) return "Never";

  const m = new Date(str);
  return format(m, "MMM dd, yyyy");
};

// 2/6/2021
export const FormatAbbrDateMonthYear = (
  str: string | null | undefined,
): string => {
  if (str == null) return "";
  if (str == "") return str;
  const year = getYear(new Date(str));
  if (year <= 1) return "Never";

  const m = new Date(str);
  return format(m, "MM/dd/yy");
};

// June 30
export const FormatDay = (str: string | null | undefined): string => {
  if (str == null) return "";
  if (str == "") return str;

  const m = new Date(str);
  if (getYear(m) < thisYear || getYear(m) > thisYear)
    return format(m, "MMMM dd, yyyy");
  return format(m, "MMMM dd");
};

// An hour ago
export const FormatRelativeDate = (str: string | null | undefined): string => {
  if (str == null) return "";
  if (str == "") return str;

  const year = getYear(new Date(str));
  if (year <= 1) return "Never";

  return formatDistanceToNow(new Date(str), { addSuffix: true });
};

export const FormatRelativeDateAbbreviated = (
  str: string | null = null,
): string => {
  if (str == null) return "";
  if (str == "") return str;

  const year = getYear(new Date(str));
  if (year <= 1) return "Never";

  return FormatRelativeDate(str)
    .replaceAll(" minutes", "m")
    .replaceAll(" hours", "h")
    .replaceAll(" days", "d");
};

// 08:37AM
export const FormatTime = (str: string | null): string => {
  if (str == null) return "";
  if (str == "") return str;

  return format(new Date(str), "hh:mm a");
};

export const Format = (
  str: string | null = null,
  formatStr = "MM/dd/yy, hh:mma",
) => {
  if (str == null) return "";
  if (str == "") return str;

  return format(new Date(str), formatStr);
};

// 22d ago or Never
export const formatLastUsed = (lastUsed: string | null = null) => {
  if (!lastUsed) return "Never";

  const formatted = FormatRelativeDateAbbreviated(lastUsed);
  return formatted;
};

// 03/27/24, 11:27PM
export const FormatDateAndTime = (str: string | null): string => {
  if (str == null) return "";
  if (str == "") return str;

  return format(new Date(str), "MM/dd/yy, hh:mm a");
};

// Utility function to convert minutes to hours
export const formatTimeFromMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0 && remainingMinutes > 0) {
    return `${hours}h ${remainingMinutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${remainingMinutes}m`;
  }
};

export const isBeforeToday = (
  dateString: string | Date | null | undefined,
): boolean => {
  if (!dateString) {
    return false;
  }

  const today = new Date();
  const targetDate = new Date(dateString);

  if (isNaN(targetDate.getTime())) {
    return false;
  }

  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  return targetDate < today;
};

export const FormatRelativeDateWithTime = (
  str: string | null | undefined,
): string => {
  if (str == null) return "";
  if (str == "") return str;

  return `${FormatRelativeDate(str)} - ${FormatTime(str)}`;
};

// intake a number of minutes and round it to the neareset .5 of an hour
export function roundMinutesToNearestHalf(number: number): number {
  const fromMinutes = number / 60;
  const result = Math.round(fromMinutes * 2) / 2;

  return result;
}

// Context https://github.com/mondoohq/console/issues/7367
// Adapted from workarounds in https://github.com/date-fns/date-fns/issues/571
export function subDaysDstSafe(date: Date, amount: number) {
  const originalTZO = date.getTimezoneOffset();
  const endDate = subDays(date, amount);
  const endTZO = endDate.getTimezoneOffset();
  const dstDiff = originalTZO - endTZO;
  return dstDiff ? addMinutes(endDate, dstDiff) : endDate;
}

// Check if a string is a valid date string
export const isDateString = (value: string | null | undefined): boolean => {
  if (!value || typeof value !== "string") return false;
  const date = new Date(value);
  return !isNaN(date.getTime()) && date.getFullYear() > 1970; // Unix epoch is 1970-01-01, reject dates before that as likely invalid
};
